import { DayCapacity_Input, OptimizePlanRequest, OptimizePlanResponse } from "@/api";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from 'primereact/checkbox';
import { useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { excelPlanResponseAtom } from "@/atoms/optimization";
import { deriveDefaultLines, deriveLineMaster, filterExistingLines } from "@/utils/lineMaster";

interface CapacitySettingProps {
  originalResponse: OptimizePlanResponse;
  optimizeRequest: OptimizePlanRequest;
  holidayDates?: string[]; // 一斉休業日のリスト
  onBack(): void;
  onProceed(request: OptimizePlanRequest): void;
}

interface CapacityRow {
  date: string;
  dayOfWeek: string;
  capacity: number;
  amount: number;
  lines: string[];
  index: number;
  isHoliday: boolean;
}

const BASE_HOURS = 8; // 基準時間

const CapacitySetting = ({ originalResponse, optimizeRequest, holidayDates = [], onBack, onProceed }: CapacitySettingProps) => {
  const excelPlanResponse = useAtomValue(excelPlanResponseAtom);

  const itemMaster = excelPlanResponse?.itemMaster

  const lineMaster : string [] = useMemo(() => deriveLineMaster(itemMaster), [itemMaster]);

  // 初期値として既存のcapacitiesを使用、ただし休業日は0に設定
  const initialCapacities = useMemo(() => {
    const defaultLines = deriveDefaultLines(itemMaster);
    return optimizeRequest.days.map((day) => {
      const isHoliday = holidayDates.includes(day.date);
      return {
        capacity: { value: isHoliday ? 0 : day.capacity.capacity.value },
        lines: defaultLines.length ? [...defaultLines] : [...day.capacity.lines],
        amount: typeof day.capacity.amount === 'number' ? day.capacity.amount : 4000,
      } satisfies DayCapacity_Input;
    });
  }, [optimizeRequest.days, holidayDates, itemMaster]);


  const [capacities, setCapacities] = useState<DayCapacity_Input[]>(initialCapacities);

  useEffect(() => {
    setCapacities(initialCapacities)
  }, [initialCapacities])

  const toggleLine = (index: number, line: string, checked: boolean) => {
    const newCapacities = [...capacities];
    const currentLines = new Set(newCapacities[index].lines || []);
    if (checked) {
      currentLines.add(line);
    } else {
      currentLines.delete(line);
    }
    newCapacities[index] = { ...newCapacities[index], lines: Array.from(currentLines) };
    setCapacities(newCapacities);
  };

  const capacityData = useMemo(() => {
    return optimizeRequest.days.map((day, index) => {
      const date = day.date;
      const dayOfWeek = ['日', '月', '火', '水', '木', '金', '土'][new Date(date).getDay()];
      const isHoliday = holidayDates.includes(date);
      const dayCapacity = capacities[index];
      return {
        date,
        dayOfWeek,
        capacity: dayCapacity?.capacity.value ?? day.capacity.capacity.value,
        amount: dayCapacity?.amount ?? day.capacity.amount ?? 4000,
        lines: dayCapacity?.lines ?? day.capacity.lines,
        index,
        isHoliday,
      } as CapacityRow;
    });
  }, [optimizeRequest.days, capacities, holidayDates]);

  const handleCapacityChange = (index: number, value: number | null) => {
    const date = optimizeRequest.days[index].date;
    const isHoliday = holidayDates.includes(date);

    // 休業日の場合は変更を許可しない
    if (isHoliday) {
      return;
    }

    if (value !== null) {
      const newCapacities = [...capacities];
      newCapacities[index] = { ...newCapacities[index], capacity: { value } };
      setCapacities(newCapacities);
    }
  };

  const handleAmountChange = (index: number, value: number | null) => {
    if (value === null) {
      return;
    }
    const newCapacities = [...capacities];
    newCapacities[index] = { ...newCapacities[index], amount: value };
    setCapacities(newCapacities);
  };


  const handleBulkChange = (pattern: 'weekday' | 'weekend' | 'all', value: number) => {
    const newCapacities = [...capacities];
    optimizeRequest.days.forEach((day, index) => {
      const date = day.date;
      const dayOfWeek = new Date(date).getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isHoliday = holidayDates.includes(date);

      // 休業日は変更しない
      if (isHoliday) {
        return;
      }

      if (pattern === 'all' ||
          (pattern === 'weekday' && !isWeekend) ||
          (pattern === 'weekend' && isWeekend)) {
        newCapacities[index] = {
          ...newCapacities[index],
          capacity: { value },
        };
      }
    });
    setCapacities(newCapacities);
  };

  const handleLineFactorBulkChange = (pattern: 'abc_line' | 'clear_line' | 'all_line') => {
    const newCapacities = [...capacities];
    optimizeRequest.days.forEach((day, index) => {
      const date = day.date;
      const isHoliday = holidayDates.includes(date);

      // 休業日は変更しない
      if (isHoliday) return;

      if (pattern === 'abc_line') {
        // A/B/C のうち lineMaster に存在するものだけを割り当てる
        // （実在しないラインを送るとバックエンドで無視され、稼働設定と結果が食い違う）
        newCapacities[index] = {
          ...newCapacities[index],
          lines: filterExistingLines(['A','B','C'], lineMaster)
        };
      } else if (pattern === 'clear_line') {
        // ラインをクリア
        newCapacities[index] = {
          ...newCapacities[index],
          lines: []
        };
      } else if (pattern === 'all_line') {
        // 全ライン（lineMaster に存在するもの）を割り当てる
        newCapacities[index] = {
          ...newCapacities[index],
          lines: deriveDefaultLines(itemMaster) // Zを除外
        };
      }
    });
    setCapacities(newCapacities);
  };

  const handleBack = () => {
    onBack();
  };

  const handleProceed = () => {
    const updatedRequest: OptimizePlanRequest = {
      ...optimizeRequest,
      days: optimizeRequest.days.map((day, index) => ({
        ...day,
        capacity: {
          capacity: { value: capacities[index]?.capacity.value ?? day.capacity.capacity.value },
          lines: capacities[index]?.lines ?? day.capacity.lines,
          amount: capacities[index]?.amount ?? day.capacity.amount ?? 4000,
        },
      })),
    };
    onProceed(updatedRequest);
  };

  const capacityBodyTemplate = (rowData: CapacityRow) => {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex gap-2 align-items-center">
          <InputNumber
            value={rowData.capacity}
            onChange={(e) => handleCapacityChange(rowData.index, e.value)}
            min={0}
            max={24}
            step={0.1}
            suffix=" 時間"
            className="flex-1"
            disabled={rowData.isHoliday}
            style={rowData.isHoliday ? { backgroundColor: '#f5f5f5' } : {}}
          />
          {rowData.isHoliday && (
            <span className="text-xs text-500 ml-2">休業日</span>
          )}
        </div>

        <div className="flex gap-2 align-items-center">
          <InputNumber
            value={rowData.amount}
            onChange={(e) => handleAmountChange(rowData.index, e.value)}
            min={0}
            step={10}
            suffix=" 個"
            className="flex-1"
            disabled={rowData.isHoliday}
            style={rowData.isHoliday ? { backgroundColor: '#f5f5f5' } : {}}
          />
          <span className="text-xs text-500">日次生産上限</span>
        </div>

        {/* ライン選択チェックボックス */}
        <div className="flex flex-wrap gap-2">
          {lineMaster.map((line) => {
            const checked = rowData.lines?.includes(line);
            return (
              <div key={`${rowData.date}-${line}`} className="flex items-center gap-1">
                <Checkbox
                  inputId={`chk-${rowData.index}-${line}`}
                  checked={!!checked}
                  onChange={(e) => toggleLine(rowData.index, line, e.checked ?? false)}
                  disabled={rowData.isHoliday}
                />
                <label htmlFor={`chk-${rowData.index}-${line}`} className="text-sm">{line}</label>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const dateBodyTemplate = (rowData: CapacityRow) => {
    return `${rowData.date} (${rowData.dayOfWeek})`;
  };

  return (
    <div className="mt-6">
      <h3>工場稼働時間設定</h3>
      <p className="text-sm text-600 mb-4">
        各日の工場稼働時間を設定してください。一括設定ボタンを使用して効率的に設定できます。
        {holidayDates.length > 0 && (
          <span className="block mt-1 text-orange-600">
            ※ 一斉休業日（{holidayDates.length}日）は自動的に稼働時間0時間に設定されます。
          </span>
        )}
      </p>

      {/* 一括設定ボタン */}
      <div className="flex gap-2 mb-4">
        <Button
          label="平日 7時間"
          size="small"
          severity="secondary"
          onClick={() => handleBulkChange('weekday', 7.0)}
        />
        <Button
          label="土日 0時間"
          size="small"
          severity="secondary"
          onClick={() => handleBulkChange('weekend', 0.0)}
        />
        <Button
          label="全日 7時間"
          size="small"
          severity="secondary"
          onClick={() => handleBulkChange('all', 7.0)}
        />
        <Button
          label="全日 0時間"
          size="small"
          severity="secondary"
          onClick={() => handleBulkChange('all', 0.0)}
        />
        <Button
          label="全日 ABCライン"
          size="small"
          severity="secondary"
          onClick={() => handleLineFactorBulkChange('abc_line')}
        />
        <Button
          label="全日 ライン解除"
          size="small"
          severity="secondary"
          onClick={() => handleLineFactorBulkChange('clear_line')}
        />
        <Button
          label="全日 全ライン"
          size="small"
          severity="secondary"
          onClick={() => handleLineFactorBulkChange('all_line')}
        />
      </div>

      {/* 稼働時間設定テーブル */}
      <DataTable
        value={capacityData}
        size="small"
        stripedRows
        scrollable
        scrollHeight="400px"
        className="w-full"
      >
        <Column
          field="date"
          header="日付"
          body={dateBodyTemplate}
          style={{ minWidth: '150px' }}
        />
        <Column
          field="capacity"
          header="稼働時間と稼働ライン"
          body={capacityBodyTemplate}
          style={{ minWidth: '400px' }}
        />
      </DataTable>

      <div className="flex flex-row justify-content-between mt-6">
        <Button
          label="戻る"
          icon="pi pi-arrow-left"
          iconPos="left"
          size="small"
          severity="secondary"
          onClick={handleBack}
        />
        <Button
          label="次へ"
          icon="pi pi-arrow-right"
          iconPos="right"
          size="small"
          severity="success"
          onClick={handleProceed}
        />
      </div>
    </div>
  );
};

export default CapacitySetting;
